import { createDependencyContext } from "app-base/web-shell/context"
import { useContext } from "react"
import type { Context as ReactContext } from "react"

/**
 * Generic factory for a view-scoped React context and its getter hook.
 *
 * Consolidates the boilerplate repeated across the `Views/<name>/Context.tsx` files:
 *   const XContext = createContext<XState | undefined>(undefined);
 *   const GetX = () => useContext(XContext)!;
 *
 * `useGet` applies the same non-null assertion, so callers treat the context as
 * always-present (the Provider is mounted above every consumer). Each call returns
 * a fresh, independent context object.
 *
 * @example
 * const shipView = createViewContext<ShipViewState>();
 * export const ShipViewContext = shipView.Context;
 * export const GetShipView = shipView.useGet;
 */
export interface ViewContext<T> {
  Context: ReactContext<T | undefined>;
  useGet: () => T;
}

export function createViewContext<T>(name: string = "ViewContext"): ViewContext<T> {
  const dependencyContext = createDependencyContext<T>(name);
  return {
    Context: dependencyContext.Context,
    useGet: () => useContext(dependencyContext.Context)!,
  };
}
